import React from 'react';
import { Badge, Tooltip } from 'antd';

import { InlineLoading } from '../../../../components/shared';
import { PanelIntervention } from '../../../../types/geneCodes/InterventionTypes';
import './interventionIconRow.css';

interface InterventionDefinition {
	icon: string;
	tooltip: string;
}

// Transcribed from the legacy pubscripts1.js iconDefinitions block; `resistance` was material-icons
// there and this app loads only Font Awesome 6.
const INTERVENTION_DEFINITIONS: Record<string, InterventionDefinition> = {
	exercise: { icon: 'fa-solid fa-person-running', tooltip: 'benefit from aerobic exercise' },
	omega3: { icon: 'fa-solid fa-fish', tooltip: 'benefit from omega-3 supplementation' },
	vitd: { icon: 'fa-solid fa-sun', tooltip: 'benefit from vitamin D supplementation and/or sunlight' },
	resistance: { icon: 'fa-solid fa-dumbbell', tooltip: 'benefit from resistance exercise' },
	mindfulness: { icon: 'fa-solid fa-brain', tooltip: 'benefit from mindfulness or meditation' },
	bmi: { icon: 'fa-solid fa-weight-scale', tooltip: 'benefit from low BMI' },
	lowcarb: { icon: 'fa-solid fa-carrot', tooltip: 'benefit from low refined carbohydrate intake' },
	lowparticulate: { icon: 'fa-solid fa-smog', tooltip: 'benefit from avoidance of exposure to airborne particulates' },
	vegan: { icon: 'fa-solid fa-bowl-food', tooltip: 'benefit from a plant based diet' },
	folate: { icon: 'fa-solid fa-leaf', tooltip: 'benefit from increased folate intake' },
	choline: { icon: 'fa-solid fa-egg', tooltip: 'benefit from increased choline intake' },
	sleep: { icon: 'fa-solid fa-bed', tooltip: 'benefit from increased sleep hygiene' },
	curcumin: { icon: 'fa-solid fa-mortar-pestle', tooltip: 'benefit from curcumin supplementation' },
	b12: { icon: 'fa-solid fa-flask', tooltip: 'benefit from B12 supplementation' },
};

const FALLBACK_ICON = 'fa-solid fa-circle-dot';

// `interv.icon` carries `material-icons` in the legacy rows and `{code}.svg` in the labiq fixture,
// neither of which this app can render — so the DB value is used only when it is already an FA class.
export const faClassFor = (code: string, dbIcon?: string): string => {
	if (dbIcon && dbIcon.startsWith('fa-')) return dbIcon;
	return INTERVENTION_DEFINITIONS[code]?.icon ?? FALLBACK_ICON;
};

const tooltipFor = (intervention: PanelIntervention): string => {
	const suffix = INTERVENTION_DEFINITIONS[intervention.code]?.tooltip;
	const lead = `${intervention.snp_count} genetic variants in this panel`;
	return suffix ? `${lead} ${suffix}` : `${lead} — ${intervention.description}`;
};

export interface InterventionIconRowProps {
	interventions: PanelIntervention[];
	selected: string[];
	onToggle: (code: string) => void;
	onClearAll: () => void;
	onShowEvidence: (code: string) => void;
	loading?: boolean;
}

export const InterventionIconRow: React.FC<InterventionIconRowProps> = ({
	interventions,
	selected,
	onToggle,
	onClearAll,
	onShowEvidence,
	loading = false,
}) => {
	if (loading) {
		return (
			<div className="wheel-intervention-row" data-testid="wheel-intervention-row">
				<InlineLoading minHeight={48} message="Loading interventions…" />
			</div>
		);
	}

	// Order is the hook's (total_score desc, then snp_count desc) — never re-sorted here.
	const rows = (interventions ?? []).filter(intervention => intervention.snp_count > 0);
	if (!rows.length) return null;

	const selectedSet = new Set(selected ?? []);

	return (
		<div className="wheel-intervention-row" data-testid="wheel-intervention-row">
			{rows.map(intervention => {
				const isSelected = selectedSet.has(intervention.code);
				return (
					<div className="wheel-intervention-item" key={intervention.code}>
						<Tooltip
							mouseLeaveDelay={0.3}
							title={
								// The evidence affordance lives IN the tooltip now, so the row carries bare icons.
								<span
									role="button"
									tabIndex={0}
									data-testid={`wheel-intervention-evidence-${intervention.code}`}
									aria-label={`Evidence for ${intervention.description}`}
									className="wheel-intervention-tip"
									onClick={() => onShowEvidence(intervention.code)}
									onKeyDown={event => {
										if (event.key !== "Enter" && event.key !== " ") return;
										event.preventDefault();
										onShowEvidence(intervention.code);
									}}
								>
									{tooltipFor(intervention)}
									<i className="fa-solid fa-circle-info" aria-hidden="true" />
								</span>
							}
						>
							<button
								type="button"
								data-testid={`wheel-intervention-toggle-${intervention.code}`}
								aria-pressed={isSelected}
								aria-label={intervention.description}
								className={`wheel-intervention-toggle${isSelected ? ' wheel-intervention-toggle--selected' : ''}`}
								onClick={() => onToggle(intervention.code)}
							>
								<Badge className="wheel-intervention-badge" count={intervention.snp_count} overflowCount={999}>
									<i className={faClassFor(intervention.code, intervention.icon)} aria-hidden="true" />
								</Badge>
							</button>
						</Tooltip>
					</div>
				);
			})}

			{selectedSet.size > 0 && (
				<button
					type="button"
					data-testid="wheel-intervention-clear"
					className="wheel-intervention-clear"
					onClick={onClearAll}
				>
					Clear
				</button>
			)}
		</div>
	);
};

export default InterventionIconRow;
